// A steam bench's cross-section (a `BenchProfilePoint` ring: x from the wall into
// the room, y up from the floor) and the solid it makes swept along a bench band.
// Pure: plan, 3D, section and the presets window all read it.

import type {
  BenchProfilePoint,
  Vec2,
} from '../shared/types';
import { earClip } from '../shared/triangulate';

/** A tessellated profile point; `s` marks an arc sample (no corner of its own). */
export interface ProfilePt { x: number; y: number; s?: boolean }
export interface Vec3 { x: number; y: number; z: number }

/** The deepest an arc's chord strays from it (mm). */
const SAGITTA = 0.5;

function signedArea(r: readonly { x: number; y: number }[]): number {
  let a = 0;
  for (let i = 0; i < r.length; i++) { const p = r[i], q = r[(i + 1) % r.length]; a += p.x * q.y - q.x * p.y; }
  return a / 2;
}

/** The arc from `p` to `q` of DXF bulge `b`, its inner points only. */
function bulgeSamples(p: Vec2, q: Vec2, b: number): ProfilePt[] {
  const dx = q.x - p.x, dy = q.y - p.y, c = Math.hypot(dx, dy);
  if (!(c > 1e-9) || Math.abs(b) < 1e-9) return [];
  const theta = 4 * Math.atan(b);
  const k = c / 2 / Math.tan(theta / 2);
  const cx = (p.x + q.x) / 2 - (dy / c) * k, cy = (p.y + q.y) / 2 + (dx / c) * k;
  const r = Math.hypot(p.x - cx, p.y - cy);
  const step = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - SAGITTA / r)));
  const n = Math.max(2, Math.min(64, Math.ceil(Math.abs(theta) / Math.max(1e-3, step))));
  const a0 = Math.atan2(p.y - cy, p.x - cx);
  const out: ProfilePt[] = [];
  for (let i = 1; i < n; i++) {
    const a = a0 + (theta * i) / n;
    out.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), s: true });
  }
  return out;
}

/** The profile as a closed ring, arcs tessellated, counter-clockwise, no point
 *  repeated (the closing one, or two in a row). */
export function profileRing(profile: readonly BenchProfilePoint[]): ProfilePt[] {
  const out: ProfilePt[] = [];
  const n = profile.length;
  for (let i = 0; i < n; i++) {
    const p = profile[i], q = profile[(i + 1) % n];
    out.push({ x: p.x, y: p.y });
    if (p.bulge) out.push(...bulgeSamples(p, q, p.bulge));
  }
  const ring = out.filter((p, i) => { const q = out[(i + 1) % out.length]; return Math.hypot(p.x - q.x, p.y - q.y) > 1e-6; });
  return signedArea(ring) < 0 ? ring.reverse() : ring;
}

/** The profile's depth (from the wall) and height (from the floor). */
export function profileExtent(profile: readonly BenchProfilePoint[]): { width: number; height: number } {
  const r = profileRing(profile);
  return { width: Math.max(0, ...r.map((p) => p.x)), height: Math.max(0, ...r.map((p) => p.y)) };
}

/** The seat height: the highest corner over the front half of the depth — the
 *  seat's front edge, not a backrest standing at the wall. */
export function profileSeatHeight(profile: readonly BenchProfilePoint[]): number {
  const { width } = profileExtent(profile);
  const front = profile.filter((p) => p.x >= width / 2 - 1e-6);
  return front.length ? Math.max(...front.map((p) => p.y)) : profileExtent(profile).height;
}

/** The profile's area (mm²). */
export function profileArea(profile: readonly BenchProfilePoint[]): number {
  return Math.abs(signedArea(profileRing(profile)));
}

/** A profile moved so its lowest point sits on the floor and its back on the wall
 *  (x, y ≥ 0, touching both), figures rounded to 0.01 mm. */
export function normalizeProfile(profile: readonly BenchProfilePoint[]): BenchProfilePoint[] {
  const r = profileRing(profile);
  const x0 = Math.min(...r.map((p) => p.x)), y0 = Math.min(...r.map((p) => p.y));
  const rd = (v: number): number => Math.round(v * 100) / 100;
  return profile.map((p) => ({ x: rd(p.x - x0), y: rd(p.y - y0), ...(p.bulge ? { bulge: p.bulge } : {}) }));
}

/** A planar face of a swept bench: its corners (plan x, y; height z) wound to face
 *  `normal`, outward. `cap` marks an end face (the profile itself). A mesh of
 *  faces (a sculpted bench's) may say, per edge `i → i+1`, the face across it in
 *  the same list (`nb`, null on its border) and whether the edge is a crease
 *  (`crease`): an elevation hides a smooth edge between two faces it sees. */
export interface SweepFace { pts: Vec3[]; normal: Vec3; cap?: boolean; s?: boolean[]; ring?: number[]; nb?: Array<number | null>; crease?: boolean[] }

const sub2 = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const unit2 = (v: Vec2): Vec2 => { const l = Math.hypot(v.x, v.y) || 1; return { x: v.x / l, y: v.y / l }; };
const lerp2 = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

function newell(pts: readonly Vec3[]): Vec3 {
  let x = 0, y = 0, z = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    x += (a.y - b.y) * (a.z + b.z); y += (a.z - b.z) * (a.x + b.x); z += (a.x - b.x) * (a.y + b.y);
  }
  const l = Math.hypot(x, y, z);
  return l > 1e-12 ? { x: x / l, y: y / l, z: z / l } : { x: 0, y: 0, z: 0 };
}

/** A face wound to look along `want`, or null when it has no area. */
function face(pts: Vec3[], want: Vec3, extra: Partial<SweepFace> = {}): SweepFace | null {
  let n = newell(pts);
  if (n.x === 0 && n.y === 0 && n.z === 0) return null;
  if (n.x * want.x + n.y * want.y + n.z * want.z < 0) {
    pts = [...pts].reverse();
    n = { x: -n.x, y: -n.y, z: -n.z };
    if (extra.s) extra = { ...extra, s: [...extra.s].reverse() };
    if (extra.ring) extra = { ...extra, ring: [...extra.ring].reverse() };
  }
  return { pts, normal: n, ...extra };
}

/** Resample a path to `n` points by arc-length fraction (its ends kept). */
function resample(path: readonly Vec2[], n: number): Vec2[] {
  if (path.length === n) return [...path];
  const cum = [0];
  for (let i = 1; i < path.length; i++) cum.push(cum[i - 1] + Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y));
  const total = cum[cum.length - 1] || 1;
  const out: Vec2[] = [];
  for (let k = 0; k < n; k++) {
    const t = (total * k) / (n - 1);
    let i = 1;
    while (i < path.length - 1 && cum[i] < t) i++;
    const span = cum[i] - cum[i - 1] || 1;
    out.push(lerp2(path[i - 1], path[i], Math.max(0, Math.min(1, (t - cum[i - 1]) / span))));
  }
  return out;
}

/**
 * The faces of a profile swept along a bench: `pathAt(x)` is the plan line the
 * profile's depth `x` runs along (from one end of the bench to the other), so a
 * straight band gives two points and a rounded corner piece its arc. Every ring
 * edge becomes a strip of planar quads between its two ends' paths, and the two
 * ends are the profile laid on the first and last points of each path — so the
 * faces share their edges exactly and close the solid. `base` lifts it all.
 */
export function profileSweepFaces(ring: readonly ProfilePt[], pathAt: (x: number) => Vec2[], base = 0): SweepFace[] {
  if (ring.length < 3) return [];
  const raw = ring.map((p) => pathAt(p.x));
  const n = Math.max(...raw.map((p) => p.length));
  if (n < 2) return [];
  const paths = raw.map((p) => resample(p, n));
  const W = Math.max(...ring.map((p) => p.x));
  const back = resample(pathAt(0), n), front = resample(pathAt(W), n);
  const P = (k: number, j: number): Vec3 => ({ x: paths[k][j].x, y: paths[k][j].y, z: ring[k].y + base });
  const out: SweepFace[] = [];
  for (let k = 0; k < ring.length; k++) {
    const k2 = (k + 1) % ring.length;
    const ex = ring[k2].x - ring[k].x, ey = ring[k2].y - ring[k].y, el = Math.hypot(ex, ey);
    if (!(el > 1e-9)) continue;
    const nx = ey / el, ny = -ex / el; // a CCW ring's outward normal
    for (let j = 0; j + 1 < n; j++) {
      const inward = unit2(sub2(lerp2(front[j], front[j + 1], 0.5), lerp2(back[j], back[j + 1], 0.5)));
      const f = face([P(k, j), P(k, j + 1), P(k2, j + 1), P(k2, j)], { x: inward.x * nx, y: inward.y * nx, z: ny },
        { s: [!!ring[k].s, !!ring[k].s, !!ring[k2].s, !!ring[k2].s] });
      if (f) out.push(f);
    }
  }
  for (const end of [0, n - 1]) {
    const t = unit2(end === 0 ? sub2(back[0], back[1]) : sub2(back[n - 1], back[n - 2]));
    const f = face(ring.map((_p, k) => P(k, end)), { x: t.x, y: t.y, z: 0 }, { cap: true, s: ring.map((p) => !!p.s), ring: ring.map((_p, k) => k) });
    if (f) out.push(f);
  }
  return out;
}

/** The plan line at depth `x` of a straight band `[backStart, backEnd,
 *  frontEnd, frontStart]` whose depth is the profile's `width`. */
export function bandPathAt(quad: readonly Vec2[], width: number): (x: number) => Vec2[] {
  const t = (x: number): number => (width > 0 ? x / width : 0);
  return (x) => [lerp2(quad[0], quad[3], t(x)), lerp2(quad[1], quad[2], t(x))];
}

/** Flat-shaded triangles of the faces (plan x, y, height z), for a mesh. */
export function sweepMesh(faces: readonly SweepFace[], ring: readonly ProfilePt[]): { pos: number[]; nrm: number[]; uv: number[] } {
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [];
  const flat = ring.map((p) => ({ x: p.x, y: p.y }));
  const capKey = new Map<Vec2, number>(flat.map((p, k) => [p, k]));
  const capTris = earClip(flat);
  const push = (a: Vec3, b: Vec3, c: Vec3, n: Vec3): void => {
    // Each triangle keeps the face's winding.
    const ux = b.x - a.x, uy = b.y - a.y, uz = b.z - a.z, vx = c.x - a.x, vy = c.y - a.y, vz = c.z - a.z;
    const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
    if (Math.hypot(cx, cy, cz) < 1e-9) return;
    for (const p of cx * n.x + cy * n.y + cz * n.z < 0 ? [a, c, b] : [a, b, c]) {
      pos.push(p.x, p.y, p.z); nrm.push(n.x, n.y, n.z); uv.push(0, 0);
    }
  };
  for (const f of faces) {
    if (!f.cap) { push(f.pts[0], f.pts[1], f.pts[2], f.normal); push(f.pts[0], f.pts[2], f.pts[3], f.normal); continue; }
    // A cap is the ring laid on the bench's end: triangulate the ring once and
    // lay each triangle onto the cap's matching corners.
    const at = new Map<number, Vec3>();
    (f.ring ?? []).forEach((k, i) => at.set(k, f.pts[i]));
    for (const tri of capTris) {
      const [a, b, c] = tri.map((p) => at.get(capKey.get(p) ?? -1));
      if (a && b && c) push(a, b, c, f.normal);
    }
  }
  return { pos, nrm, uv };
}

/**
 * The profile where a vertical plane cuts the bench: the chord runs from depth
 * `x0` to depth `x1` (profile x), and each point comes back as `u` — 0 at the
 * chord's start, 1 at its end — and its height. A chord along the wall (one
 * depth) cuts the slice there: rectangles spanning the chord.
 */
export function profileCut(ring: readonly ProfilePt[], x0: number, x1: number): Array<Array<{ u: number; y: number; s?: boolean }>> {
  if (Math.abs(x1 - x0) < 1e-6) {
    return sliceAt(ring, x0).map(([lo, hi]) => [{ u: 0, y: lo }, { u: 1, y: lo }, { u: 1, y: hi }, { u: 0, y: hi }]);
  }
  const lo = Math.min(x0, x1), hi = Math.max(x0, x1);
  let pts: ProfilePt[] = [...ring];
  pts = clipX(pts, lo, 1);
  pts = clipX(pts, hi, -1);
  if (pts.length < 3 || Math.abs(signedArea(pts)) < 1e-6) return [];
  return [pts.map((p) => ({ u: (p.x - x0) / (x1 - x0), y: p.y, ...(p.s ? { s: true } : {}) }))];
}

/** Sutherland–Hodgman against x ≥ c (sign 1) or x ≤ c (sign −1); a point the
 *  clip line makes is a corner. */
function clipX(pts: readonly ProfilePt[], c: number, sign: 1 | -1): ProfilePt[] {
  const out: ProfilePt[] = [];
  const inside = (p: ProfilePt): boolean => sign * (p.x - c) >= -1e-9;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const ia = inside(a), ib = inside(b);
    if (ia) out.push(a);
    if (ia !== ib) {
      const t = (c - a.x) / (b.x - a.x);
      out.push({ x: c, y: a.y + (b.y - a.y) * t });
    }
  }
  return out;
}

/** The heights the profile covers on the vertical line at depth `x`. */
export function sliceAt(ring: readonly ProfilePt[], x: number): Array<[number, number]> {
  const ys: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    if ((a.x <= x && b.x > x) || (b.x <= x && a.x > x)) ys.push(a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x));
  }
  ys.sort((p, q) => p - q);
  const out: Array<[number, number]> = [];
  for (let i = 0; i + 1 < ys.length; i += 2) out.push([ys[i], ys[i + 1]]);
  return out;
}

/** How far (mm) `sweepPlaneCut` moves its plane off the one asked for. */
const CUT_NUDGE = 1.3e-6;

/**
 * Where a vertical plane cuts a swept bench, from its faces: every face crossed
 * gives the segments the plane cuts from it, and the segments chained end to end
 * are the cut's closed outlines — so it holds for any sweep, round a rounded
 * corner's arc too (`profileCut` reads a straight band directly). The plane runs
 * through `a` along `u` (horizontal, unit) with normal `n`; each point comes back
 * as `t` along `u` and its height, `s` where it lies between two arc samples.
 */
export function sweepPlaneCut(faces: readonly SweepFace[], a: Vec2, u: Vec2, n: Vec2): Array<Array<{ t: number; h: number; s?: boolean }>> {
  type P = { t: number; h: number; s?: boolean };
  const segs: Array<[P, P]> = [];
  for (const f of faces) {
    // Moved a micrometre off, so a plane along a seam between two faces (a cut
    // through an arc's sample) crosses one of them instead of touching both.
    const d = f.pts.map((p) => (p.x - a.x) * n.x + (p.y - a.y) * n.y - CUT_NUDGE);
    if (d.every((v) => v >= 0) || d.every((v) => v < 0)) continue;
    const hits: P[] = [];
    for (let i = 0; i < f.pts.length; i++) {
      const j = (i + 1) % f.pts.length;
      if ((d[i] < 0) === (d[j] < 0)) continue;
      const k = d[i] / (d[i] - d[j]);
      const p = f.pts[i], q = f.pts[j];
      const x = p.x + (q.x - p.x) * k, y = p.y + (q.y - p.y) * k;
      hits.push({ t: (x - a.x) * u.x + (y - a.y) * u.y, h: p.z + (q.z - p.z) * k, ...(f.s?.[i] && f.s?.[j] ? { s: true } : {}) });
    }
    // A planar face meets the plane on one line: its crossings pair up in order along it.
    const far = hits.reduce((m, p) => (Math.hypot(p.t - hits[0].t, p.h - hits[0].h) > Math.hypot(m.t - hits[0].t, m.h - hits[0].h) ? p : m), hits[0]);
    const lt = far.t - hits[0].t, lh = far.h - hits[0].h;
    hits.sort((p, q) => (p.t - q.t) * lt + (p.h - q.h) * lh);
    for (let i = 0; i + 1 < hits.length; i += 2) segs.push([hits[i], hits[i + 1]]);
  }
  const key = (p: P): string => `${Math.round(p.t * 1e5)},${Math.round(p.h * 1e5)}`;
  const at = new Map<string, number[]>();
  segs.forEach(([p, q], i) => { for (const e of [p, q]) { const k = key(e); at.set(k, [...(at.get(k) ?? []), i]); } });
  const used = new Set<number>();
  const loops: P[][] = [];
  for (let s0 = 0; s0 < segs.length; s0++) {
    if (used.has(s0)) continue;
    used.add(s0);
    const loop: P[] = [segs[s0][0]];
    let end = segs[s0][1];
    for (let guard = 0; guard < segs.length; guard++) {
      if (key(end) === key(loop[0])) break;
      loop.push(end);
      const next = (at.get(key(end)) ?? []).find((i) => !used.has(i));
      if (next == null) break;
      used.add(next);
      end = key(segs[next][0]) === key(end) ? segs[next][1] : segs[next][0];
    }
    if (loop.length >= 3) loops.push(loop);
  }
  return loops;
}

/** The corners within this of a profile's lowest / highest reach count as on it (mm). */
const LEVEL_EPS = 0.5;

/**
 * How two profiled tiers meet, the higher one toward the wall: the higher
 * bench's innermost LOWEST corner sits on the lower bench's outermost HIGHEST
 * corner. `dx` is where the lower profile's own x = 0 lands in the higher one's
 * frame (the higher bench's wall side at 0), `dy` how far the higher one rises
 * above the lower one. Corners only — a bulge's samples are no corner.
 */
export function profileStep(upper: readonly BenchProfilePoint[], lower: readonly BenchProfilePoint[]): { dx: number; dy: number } {
  const yLo = Math.min(...upper.map((p) => p.y));
  const foot = upper.filter((p) => p.y <= yLo + LEVEL_EPS).reduce((a, p) => (p.x > a.x ? p : a));
  const yHi = Math.max(...lower.map((p) => p.y));
  const top = lower.filter((p) => p.y >= yHi - LEVEL_EPS).reduce((a, p) => (p.x < a.x ? p : a));
  return { dx: foot.x - top.x, dy: top.y - foot.y };
}

/**
 * A profile raised by `lift` with the gap under it filled to the floor: every
 * run of corners on its lowest line (joined by straight edges) drops to y = 0
 * at both ends, so the bench stands on a solid as wide as its bottom edge. The
 * ring keeps its winding and its bulges; `lift` ≤ 0 returns the profile itself.
 */
export function profileFilled(profile: readonly BenchProfilePoint[], lift: number): BenchProfilePoint[] {
  if (!(lift > 1e-6) || profile.length < 3) return [...profile];
  const n = profile.length;
  const yLo = Math.min(...profile.map((p) => p.y));
  const low = profile.map((p) => p.y <= yLo + 1e-6);
  const joined = (i: number): boolean => low[i] && low[(i + 1) % n] && !profile[i].bulge;
  // Start just after a corner that does not open a bottom run, so no run wraps.
  let start = profile.findIndex((_, i) => !joined((i - 1 + n) % n));
  if (start < 0) return profile.map((p) => ({ ...p, y: p.y + lift }));
  const out: BenchProfilePoint[] = [];
  for (let s = 0; s < n; s++) {
    const i = (start + s) % n;
    const p = profile[i];
    if (!joined(i)) { out.push({ ...p, y: p.y + lift }); continue; }
    let j = i;
    while (joined(j) && (j + 1) % n !== (start + n) % n) j = (j + 1) % n;
    const last = profile[j];
    out.push({ x: p.x, y: p.y + lift }, { x: p.x, y: 0 }, { x: last.x, y: 0 });
    s += (j - i + n) % n - 1;
  }
  return out;
}

// ---- A profile in the drawing editor (ui/profileEditor.ts) ----
// The editor's polygon holds points only, so a profile's arcs go in as their
// samples and come back as bulges wherever those samples are still there.

/** An arc of a profile as the editor's polygon holds it: its ends and the
 *  points between them, in the profile's frame. */
export interface ProfileArc { from: Vec2; to: Vec2; bulge: number; inner: Vec2[] }

/** A profile as one polygon's points, in its own order (arcs as their
 *  samples), and the arcs to give back by `outlineProfile`. */
export function profileOutline(profile: readonly BenchProfilePoint[]): { points: Vec2[]; arcs: ProfileArc[] } {
  const points: Vec2[] = [], arcs: ProfileArc[] = [];
  profile.forEach((p, i) => {
    const q = profile[(i + 1) % profile.length];
    points.push({ x: p.x, y: p.y });
    if (!p.bulge) return;
    const inner = bulgeSamples(p, q, p.bulge).map((s) => ({ x: s.x, y: s.y }));
    points.push(...inner);
    if (inner.length) arcs.push({ from: { x: p.x, y: p.y }, to: { x: q.x, y: q.y }, bulge: p.bulge, inner });
  });
  return { points, arcs };
}

const ARC_TOL = 0.05;

/** The polygon's points back as a profile: each arc whose ends and samples are
 *  all still there in a row (moved together, by any shift) is one bulged
 *  corner again; an arc with one of them moved stays its points. */
export function outlineProfile(points: readonly Vec2[], arcs: readonly ProfileArc[]): BenchProfilePoint[] {
  const n = points.length;
  const near = (a: Vec2, b: Vec2, dx: number, dy: number): boolean => Math.abs(a.x - b.x - dx) <= ARC_TOL && Math.abs(a.y - b.y - dy) <= ARC_TOL;
  const bulge = new Map<number, number>(), inner = new Set<number>();
  for (const arc of arcs) {
    const m = arc.inner.length;
    if (m + 2 > n) continue;
    for (let s = 0; s < n; s++) {
      if (bulge.has(s) || inner.has(s)) continue;
      const dx = points[s].x - arc.from.x, dy = points[s].y - arc.from.y;
      const ok = arc.inner.every((q, k) => near(points[(s + 1 + k) % n], q, dx, dy)) && near(points[(s + 1 + m) % n], arc.to, dx, dy);
      if (!ok) continue;
      bulge.set(s, arc.bulge);
      for (let k = 1; k <= m; k++) inner.add((s + k) % n);
      break;
    }
  }
  const out: BenchProfilePoint[] = [];
  points.forEach((p, i) => {
    if (inner.has(i)) return;
    const b = bulge.get(i);
    out.push({ x: p.x, y: p.y, ...(b ? { bulge: b } : {}) });
  });
  return out;
}

/** The shift that stands points on the floor (lowest y = 0) against the wall
 *  (smallest x = 0): a profile's two constraints. */
export function floorAndWallShift(points: readonly Vec2[]): { dx: number; dy: number } {
  if (!points.length) return { dx: 0, dy: 0 };
  return { dx: -Math.min(...points.map((p) => p.x)), dy: -Math.min(...points.map((p) => p.y)) };
}

function segmentsCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o = (p: Vec2, q: Vec2, r: Vec2): number => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = o(c, d, a), d2 = o(c, d, b), d3 = o(a, b, c), d4 = o(a, b, d);
  return ((d1 > 1e-9 && d2 < -1e-9) || (d1 < -1e-9 && d2 > 1e-9)) && ((d3 > 1e-9 && d4 < -1e-9) || (d3 < -1e-9 && d4 > 1e-9));
}

/** What a profile drawn in the editor needs before it can be one (English, the
 *  dictionary key), or null: one closed outline — no open line, no second
 *  outline, no hole — that does not cross itself. */
export function profileOutlineProblem(outlines: ReadonlyArray<{ points: readonly Vec2[]; holes?: readonly Vec2[][] }>, openLines: number): string | null {
  if (openLines > 0) return 'A bench profile is a closed outline: delete the open lines.';
  if (!outlines.length) return 'Draw the profile as one closed outline, with Polygon or Rectangle.';
  if (outlines.length > 1) return 'A bench profile is one closed outline: delete the others.';
  const o = outlines[0];
  if (o.holes?.some((h) => h.length)) return 'A bench profile has no holes.';
  const pts = o.points, n = pts.length;
  if (n < 3 || Math.abs(signedArea(pts)) < 1) return 'The outline encloses no area.';
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (segmentsCross(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n])) return 'The outline crosses itself.';
    }
  }
  return null;
}
